import type { components } from "@oathera/contract/api";
import type { Service } from "@oathera/contract";
import type { Measure } from "../../components/index.ts";
import type { StatusLevel } from "../../components/index.ts";

/**
 * Small, shared vocabulary for the Overview screen: how a contract `Quantity`/`exactness`/
 * `ServiceStatus` maps onto the screen's presentational pieces, plus the KYA service-map layout.
 *
 * This lives apart from the components so every region (tiles, map) labels numbers and statuses the
 * same way, and so the mappings — the one place design 12.3 ("counters exact, estimates say 'est.'";
 * "unverifiable is never green") is encoded for this screen — are testable in isolation.
 */

type Quantity = components["schemas"]["Quantity"];
type ServiceStatus = components["schemas"]["ServiceStatus"];

/**
 * The KYA-flow components design 12.2 shows, laid out as a 3-column grid (row 1: IDP, AIS, VCI; row
 * 2: Gateway, Identity MCP, Resource Server). Each maps to a contract `Service` enum member. The contract also defines `sim`, which is not part of the headline KYA grid; the overview response
 * may carry health for it and the screen lists it separately so nothing the server reports is
 * silently dropped.
 */
export interface KyaNode {
  readonly service: Service;
  readonly label: string;
}

export const KYA_FLOW: readonly KyaNode[] = [
  // Rendered as a 3-column grid, row by row: row 1 = IDP, AIS, VCI; row 2 = Gateway MCP, Identity MCP,
  // Resource Server(s). Identity MCP is part of the flow grid here (not an "extra"), matching the KYA
  // request/response path across the two rows.
  { service: "idp", label: "IDP" },
  { service: "ais", label: "AIS" },
  { service: "vci", label: "VCI" },
  { service: "gateway-mcp", label: "Gateway MCP" },
  { service: "identity-mcp", label: "Identity MCP" },
  { service: "resource", label: "Resource Server(s)" },
];

const KYA_SERVICES: ReadonlySet<Service> = new Set(KYA_FLOW.map((n) => n.service));

/** True when a service is one of the five headline KYA-flow nodes. */
export function isKyaService(service: Service): boolean {
  return KYA_SERVICES.has(service);
}

/** A human label for any service, including the non-KYA ones the response may carry. */
export function serviceLabel(service: Service): string {
  const node = KYA_FLOW.find((n) => n.service === service);
  if (node !== undefined) return node.label;
  switch (service) {
    case "identity-mcp":
      return "Identity MCP";
    case "sim":
      return "Simulator";
    default:
      return service;
  }
}

/**
 * Map a contract `ServiceStatus` onto a {@link StatusBadge} level. Crucially `unknown` and `silent`
 * are grey (`unknown`), never the healthy green — an unconfirmed or silent service must not read as
 * "all good" (design 12.3). `degraded` is a warning, `down` an error.
 */
export function statusLevel(status: ServiceStatus): StatusLevel {
  switch (status) {
    case "healthy":
      return "healthy";
    case "degraded":
      return "warn";
    case "down":
      return "error";
    case "silent":
    case "unknown":
      return "unknown";
  }
}

/** The visible label for a service status (icon + text; the badge supplies the icon). */
export function statusText(status: ServiceStatus): string {
  switch (status) {
    case "healthy":
      return "Healthy";
    case "degraded":
      return "Degraded";
    case "down":
      return "Down";
    case "silent":
      return "Silent";
    case "unknown":
      return "Unknown";
  }
}

/** Map a `Quantity`'s exactness onto the shared exact/estimate vocabulary (design 12.3). */
export function quantityMeasure(q: Quantity): Measure {
  // A quantity with no data cannot be verified, so it is neither an exact counter nor a sampled
  // estimate — it is unverifiable (and so never rendered green).
  if (q.value === null) return "unverifiable";
  return q.exactness === "estimated" ? "estimate" : "exact";
}
